import axios from '@/plugins/axios'
import { Module } from 'vuex'
import { sortResults, sortCollection } from '@/utils/collections'

const reportModule: Module<any, any> = {
    state: {
        filters: {},
    },
    actions: {
        async initReportIndex() {
            if (this.reportIndexLoading) {
        		await this.initReportPromise
        		return
        	}
            this.reportIndexLoading = true
            this.initReportPromise = this.dispatch('initIndex', {
                id: 'report',
                idField: 'id',
                indexedFields: [
                    'name',
                    'reportType',
                ],
                source: '/api/reports',
                refresh: true,
            })
            await this.initReportPromise
            this.reportIndexLoading = false
            this.initReportPromise = null
        },

        async getReports(_, params) {
            const indexExists = await this.dispatch(
                'indexExists',
                'report',
            )
            if (!indexExists || params?.refresh) {
                await this.dispatch('initReportIndex')
            }

            const reportTypeFilter = (items) => {
                const filter = params?.filters?.reportType
                return filter
                    ? items?.filter((e) => e.reportType===filter)
                    : items
            }

            const processingEnabledFilter = (items) => {
                return items?.filter(i => params?.filters?.processingEnabled === i.processingEnabled)
            }

            const activeFilter = (items) => {
                return items?.filter((e) => !e.deletedAt)
            }


            const profileTypeFilter = (items) => {
            	const filter = params?.filters?.profileType
            	if (filter === 'person') {
                	return items?.filter(i => i.targetsPersonalProfiles)
                } else if (filter === 'organization') {
                	return items?.filter(i => i.targetsOrganizationProfiles)
                }
                return items
            }

            const trackingTypeFilter = (items) => {
            	const filter = params?.filters?.trackingType
            	if (filter === 'activity') {
                	return items?.filter(i => i.tracksActivity)
                } else if (filter === 'mentions') {
                	return items?.filter(i => i.tracksMentions)
                } else if (filter === 'keywords') {
                	return items?.filter(i => i.tracksKeywords)
                }
                return items
            }

            const sortName = (items) => {
                return sortCollection(items, 'name', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortUpdatedAt = (items) => {
                return sortResults(items, 'updatedAt', params?.sortOrder)
            }

            const getSortOrder = () => {
                if (params?.sortBy === 'updatedAt') {
                    return sortUpdatedAt
                } else {
                    return sortName
                }
            }

            let records = null
            records = await this.dispatch('getRecords', {
                index: 'report',
                term: params?.search,
                fields: ['name', 'reportType'],
                page: params?.page ?? 1,
                perPage: params?.perPage ?? 10,
                filters: [reportTypeFilter, trackingTypeFilter, activeFilter, profileTypeFilter, processingEnabledFilter],
                sort: getSortOrder(),
            })

            return records
        },

        async getReport(_, id) {
        
            if (id === undefined || id === null) {
                throw new Error(`Invalid Report ID specified: ${id}`)
            }

            const indexExists = await this.dispatch('indexExists', 'report')

            if (indexExists) {
                return await this.dispatch('getRecord', {
                    index: 'report',
                    id,
                })
            } else {
                return await this.dispatch('getReportFresh', id)
            }
        },

        async getReportFresh(_, id) {
            const indexExists = await this.dispatch('indexExists', 'report')

            const { data } = await axios.get(`/api/reports/${id}`)
            if (!indexExists) {
                await this.dispatch('initReportIndex')
            }

            if (data) {
                await this.dispatch('addRecord', {
                    index: 'report',
                    record: data,
                })
            }

            return data
        },

        async addReport(_, params) {
            await axios.post('/api/reports', params)
        },

        async updateReport(_, params) {
            await axios.put(`/api/reports/${params.id}`, params)
        },

        async manualUpdateReport(_, id) {
            await axios.put(`/api/reports/update/${id}`, {})
        },

        async disableReport(_, id) {
            await axios.put(`/api/reports/${id}/disable`, {})
        },

        async enableReport(_, id) {
            await axios.put(`/api/reports/${id}/enable`, {})
        },

        async deleteReport(_, id) {
            await axios.delete(`/api/reports/${id}`)
        },

        async addReportProfile(_, params) {
            await axios.put(`/api/reports/${params.id}/add_profile`, params)
        },

        async removeReportProfile(_, params) {
            await axios.put(`/api/reports/${params.id}/remove_profile`, params)
        },

        async addReportKeyword(_, params) {
            await axios.put(`/api/reports/${params.id}/add_keyword`, {keyword: params.keyword})
        },

        async removeReportKeyword(_, params) {
            await axios.put(`/api/reports/${params.id}/remove_keyword`, {keyword: params.keyword})
        },

        async emailReport(_, params) {
            await axios.put(`/api/reports/${params.id}/email_report`, params)
        },

        async generateStaticImage(_, params) {
            const workspaceUuid = params.workspaceUuid ? '&workspaceUuid=' + params.workspaceUuid : ''
            const exportFormat =  params.exportFormat ? '&exportFormat=' + params.exportFormat : ''
            const leaderBy =  params.leaderBy ? '&leaderBy=' + params.leaderBy : ''            
            const period =  params.period ? '&period=' + params.period : ''
            const start = params.start ? `&start=${params.start}`: ''
            const end =  params.end ? `&end=${params.end}` : ''
            const lowerLimit =  params.lowerLimit ? '&lowerLimit=' + params.lowerLimit : ''
            const upperLimit =  params.upperLimit ? '&upperLimit=' + params.upperLimit : ''
            const { data } = await axios.get('/api/reports/generate_static_image?id=' + params.id + workspaceUuid + exportFormat + leaderBy + period + start + end + lowerLimit + upperLimit, 
                {
                    responseType: 'blob' // Important to handle binary data
                }
            )
            return data
        },

        async uploadCSV(_, params) {
            const { data } = await axios.post('/api/reports/upload_csv', params, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              })
            return data
        },
    },
}

export default reportModule
